import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeSv from '@angular/common/locales/sv';
import { BrowserModule } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';

import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AssetTranslateLoader } from './i18n/asset-translate.loader';
import { LANG_CODE_KEY } from './i18n/i18n.service';
import { ApiModule } from './services/api/api.module';
import { apiAuthInterceptor } from './services/auth/auth.interceptor';

// `en-US` ships with the framework; the other three have to be registered or
// `| date` throws NG02100 the first time somebody switches language.
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeSv);

export const createTranslateLoader = (http: HttpClient) => new AssetTranslateLoader(http);

/** Language code as stored → the Angular locale id registered above. */
const ANGULAR_LOCALES: Record<string, string> = { en: 'en-US', de: 'de', es: 'es', sv: 'sv' };

/**
 * Reads `localStorage` directly rather than asking `I18nService`.
 *
 * `LOCALE_ID` is resolved while the injector is being built, before any service
 * exists — there is nothing to ask. `I18nService` writes the same key and
 * reloads the page when it changes, so the two cannot drift.
 */
export function localeIdFactory(): string {
  try {
    return ANGULAR_LOCALES[localStorage.getItem(LANG_CODE_KEY) ?? 'en'] ?? 'en-US';
  } catch {
    return 'en-US';
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    IonicModule.forRoot({ mode: 'ios' }),
    ApiModule.forRoot({ rootUrl: environment.apiUrl }),
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useFactory: createTranslateLoader, deps: [HttpClient] },
    }),
    // Web only, so no SQLite driver: there is no native build of this app.
    IonicStorageModule.forRoot({ driverOrder: [Drivers.LocalStorage] }),
    // Only on the deployed builds. `ng serve` rebuilds on every save, and a worker
    // caching that output would serve the previous edit back.
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production || environment.name == 'beta',
    }),
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: LOCALE_ID, useFactory: localeIdFactory },
    provideHttpClient(withInterceptors([apiAuthInterceptor])),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
